import { FormEvent, useCallback, useEffect, useState } from 'react';
import { ApiRequestError } from '../../api/api-client';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Badge } from '../../components/atoms/Badge';
import { EmptyState, ErrorState, LoadingState } from '../../components/molecules/FeedbackState';
import { Modal } from '../../components/molecules/Modal';
import { Table, TableColumn } from '../../components/organisms/Table';
import { usePermission } from '../../auth/permissions';
import { PaginatedResponse, PaginationQuery } from '../../types/api';

interface ActiveRecord { id: number; active: boolean; }
interface FieldConfig { key: string; label: string; type?: 'text' | 'email' | 'textarea'; required?: boolean; placeholder?: string; }

interface CatalogResourcePageProps<Row extends ActiveRecord, FormValues extends object> {
  title: string;
  description: string;
  permissionPrefix: string;
  fields: FieldConfig[];
  columns: TableColumn<Row>[];
  initialValues: FormValues;
  list: (query: PaginationQuery & { active?: boolean }) => Promise<PaginatedResponse<Row>>;
  create: (input: FormValues) => Promise<Row>;
  update: (id: number, input: Partial<FormValues>) => Promise<Row>;
  updateStatus: (id: number, active: boolean) => Promise<Row>;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  return 'The request could not be completed. Please try again.';
}

function cleanForm<T extends object>(form: T): T {
  return Object.fromEntries(Object.entries(form).filter(([, value]) => value !== '')) as T;
}

export function CatalogResourcePage<Row extends ActiveRecord, FormValues extends object>({
  title, description, permissionPrefix, fields, columns, initialValues, list, create, update, updateStatus,
}: CatalogResourcePageProps<Row, FormValues>) {
  const canCreate = usePermission(`${permissionPrefix}.create`);
  const canUpdate = usePermission(`${permissionPrefix}.update`);
  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<PaginatedResponse<Row>['meta']>({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [query, setQuery] = useState({ search: '', page: 1, pageSize: 20, active: 'all' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<FormValues>(initialValues);
  const [isSaving, setIsSaving] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState<number | null>(null);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await list({ ...query, active: query.active === 'all' ? undefined : query.active === 'true' });
      setRows(response.data);
      setMeta(response.meta);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [list, query]);

  useEffect(() => { void loadRows(); }, [loadRows]);

  const openCreate = () => { setEditing(null); setForm(initialValues); setIsModalOpen(true); };
  const openEdit = (row: Row) => {
    setEditing(row);
    setForm(Object.fromEntries(fields.map(({ key }) => [key, (row as Record<string, unknown>)[key] ?? ''])) as FormValues);
    setIsModalOpen(true);
  };
  const closeModal = () => { if (!isSaving) { setEditing(null); setIsModalOpen(false); } };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      if (editing) await update(editing.id, cleanForm(form));
      else await create(cleanForm(form));
      setEditing(null);
      setIsModalOpen(false);
      await loadRows();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  const handleStatus = async (row: Row) => {
    setIsStatusUpdating(row.id);
    setError(null);
    try { await updateStatus(row.id, !row.active); await loadRows(); }
    catch (requestError) { setError(errorMessage(requestError)); }
    finally { setIsStatusUpdating(null); }
  };

  const actionColumn: TableColumn<Row> = {
    key: 'actions', header: 'Actions', render: (row) => canUpdate ? (
      <div className="table-actions">
        <Button variant="ghost" type="button" onClick={() => openEdit(row)}>Edit</Button>
        <Button variant="ghost" type="button" disabled={isStatusUpdating === row.id} onClick={() => void handleStatus(row)}>
          {isStatusUpdating === row.id ? 'Saving…' : row.active ? 'Deactivate' : 'Activate'}
        </Button>
      </div>
    ) : null,
  };

  return (
    <section className="resource-page">
      <div className="page-toolbar">
        <div><p className="eyebrow">Catalog</p><h2>{title}</h2><p className="muted">{description}</p></div>
        {canCreate && <Button type="button" onClick={openCreate}>Add {title.replace(/s$/, '')}</Button>}
      </div>
      <div className="filters-bar">
        <Input value={query.search} placeholder={`Search ${title.toLowerCase()}…`} onChange={(event) => setQuery((current) => ({ ...current, search: event.target.value, page: 1 }))} />
        <select className="input filter-select" value={query.active} onChange={(event) => setQuery((current) => ({ ...current, active: event.target.value, page: 1 }))} aria-label="Status filter">
          <option value="all">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
        </select>
      </div>
      {error && <ErrorState message={error} />}
      {isLoading ? <LoadingState /> : rows.length === 0 ? <EmptyState message={`No ${title.toLowerCase()} found.`} /> : <Table columns={[...columns, actionColumn]} rows={rows} rowKey={(row) => row.id} />}
      {meta.totalPages > 1 && <div className="pagination"><Button variant="secondary" type="button" disabled={query.page <= 1} onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}>Previous</Button><span>Page {meta.page} of {meta.totalPages}</span><Button variant="secondary" type="button" disabled={query.page >= meta.totalPages} onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}>Next</Button></div>}
      {isModalOpen && <Modal title={editing ? `Edit ${title.replace(/s$/, '')}` : `Add ${title.replace(/s$/, '')}`} onClose={closeModal}>
        <form className="modal-form" onSubmit={handleSubmit}>
          {fields.map((field) => <label className="form-field" key={field.key}>{field.label}{field.type === 'textarea' ? <textarea className="input" value={String((form as Record<string, unknown>)[field.key] ?? '')} placeholder={field.placeholder} required={field.required} onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))} /> : <Input type={field.type ?? 'text'} value={String((form as Record<string, unknown>)[field.key] ?? '')} placeholder={field.placeholder} required={field.required} onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))} />}</label>)}
          <div className="modal-actions"><Button variant="secondary" type="button" onClick={closeModal}>Cancel</Button><Button type="submit" disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</Button></div>
        </form>
      </Modal>}
    </section>
  );
}

export function activeBadge(active: boolean) { return <Badge tone={active ? 'success' : 'neutral'}>{active ? 'Active' : 'Inactive'}</Badge>; }
